// No imports here: the tests load this file directly with node --test.

// Any shift location other than its event's counts as the area — including another main location, to match the
// Edit Shift modal's "Area" field.
export const getShiftAreaId = ({ areaId, locationId }) => (!areaId || areaId === locationId ? null : areaId);

// Raw project shifts hold the area in locationId; booked slots from useProjects hold the event
// location in locationId and the area in areaId.
export const getShiftPlaceIds = ({ locationId, areaId, projectLocationId, isRawShifts }) => {
  const mainLocationId = isRawShifts ? projectLocationId : locationId;

  return {
    mainLocationId,
    areaId: getShiftAreaId({ areaId: isRawShifts ? locationId : areaId, locationId: mainLocationId })
  };
};

export const getShiftArea = ({ areaId, locationId, mappedLocations }) =>
  mappedLocations?.[getShiftAreaId({ areaId, locationId })];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const wordRegexes = new Map();

const getWordRegex = (word) => {
  if (!wordRegexes.has(word)) {
    wordRegexes.set(word, new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(word)}($|[^\\p{L}\\p{N}])`, 'iu'));
  }
  return wordRegexes.get(word);
};

const includesWord = (text, word) => Boolean(text && word && getWordRegex(word).test(text));

export const getShiftLocationTitle = ({ clientName, locationName, areaName, displayName }) => {
  const isSameName = areaName?.toLowerCase() === locationName?.toLowerCase();
  const places = [locationName, isSameName ? null : areaName].filter(Boolean);
  const placesNotInName = places.filter((name) => !includesWord(displayName, name));
  const placeTitle = (placesNotInName.length || clientName ? placesNotInName : places.slice(0, 1)).join(' · ');

  return [clientName, placeTitle].filter(Boolean).join(', ') || '—';
};

export const isMatchingLocationFilters = ({ mainLocationId, areaId, mainLocationIds, areaIds }) => {
  if (mainLocationIds && !mainLocationIds.includes(mainLocationId)) return false;
  if (areaIds && !areaIds.includes(areaId)) return false;
  return true;
};

export const mergeFilterOptions = (...optionLists) => {
  const seenIds = new Set();

  return optionLists.flat().filter((option) => {
    if (!option || option.value == null || seenIds.has(option.id)) return false;
    seenIds.add(option.id);
    return true;
  });
};

export const toFilterOptions = (selectOptions = []) =>
  selectOptions.map(({ value, label }) => ({ id: value, value: label }));

export const collectWeekShiftsAndLocations = ({ projects = [], mappedLocations = {} }) => {
  const shifts = [];
  const mainLocations = [];
  const areas = [];

  projects.forEach(({ locationId: projectLocationId, shifts: projectShifts = [] }) => {
    if (mappedLocations[projectLocationId]) {
      mainLocations.push({ id: projectLocationId, value: mappedLocations[projectLocationId].name });
    }

    projectShifts.forEach((shift) => {
      shifts.push(shift);
      const areaId = getShiftAreaId({ areaId: shift.locationId, locationId: projectLocationId });
      if (areaId && mappedLocations[areaId]) {
        areas.push({ id: areaId, value: mappedLocations[areaId].name });
      }
    });
  });

  return {
    shifts,
    mainLocations: mergeFilterOptions(mainLocations),
    areas: mergeFilterOptions(areas)
  };
};

// Branch main locations (or every account location in all-branches mode) and every account area —
// the locations API has no area → parent link, so areas can't be scoped to the branch.
export const getLocationAndAreaFilterOptions = ({ locations = [], mappedLocations = {}, showAllBranches = false }) => {
  const mainLocations = [];
  const areas = [];

  (showAllBranches ? Object.values(mappedLocations) : locations).forEach(
    ({ id, name, isSubLocation, isSubLocationDeleted, hasActiveBranch }) => {
      if (isSubLocation) {
        if (!isSubLocationDeleted) areas.push({ id, value: name });
        return;
      }
      if (hasActiveBranch === false) return;
      mainLocations.push({ id, value: name });
    }
  );

  return { mainLocations, areas };
};

// DEVS-1201: the board's old "Areas" filter slug can still be saved in localStorage. formatShiftsFilters still
// applies it, so it must never be left behind with no chip. Its main locations move to Locations; areas and null
// ("Other areas") are dropped, because Locations + Areas are AND-ed and would narrow the old OR-ed selection.
// Main locations can't be told apart before locations load, so until then the old entry is kept as is.
export const migrateLegacyLocationsFilter = ({ filters, legacySlug, mainLocationsSlug, mappedLocations }) => {
  const legacy = Array.isArray(filters?.basic) && filters.basic.find(({ slug }) => slug === legacySlug);
  if (!legacy || !Object.keys(mappedLocations || {}).length) return filters;

  const rest = filters.basic.filter(({ slug }) => slug !== legacySlug);
  const mainLocationIds = []
    .concat(legacy.value)
    .filter((id) => mappedLocations[id] && !mappedLocations[id].isSubLocation);
  const hasLocationsFilter = rest.some(({ slug }) => slug === mainLocationsSlug);
  if (!mainLocationIds.length || hasLocationsFilter) return { ...filters, basic: rest };

  return { ...filters, basic: [...rest, { ...legacy, slug: mainLocationsSlug, value: mainLocationIds }] };
};
